import { useInstance } from '@meraki-internal/react-dependency-injection';
import { INote } from '../INote';
import { INoteTarget } from '../INoteTarget';
import { INoteTemplate } from './TemplatesConfig';
import { TemplateProvider } from './TemplateProvider';
import { MixPanelEventEmitter } from '../../../metrics/MixPanelEventEmitter';
import { IonButton } from '@ionic/react';
import { getNoteTemplateTrackingLocation } from '../NotesTrackingHelper';
import { AlertPresenter } from '../../../AlertBinder';
import { NoteTemplateMenu } from './NoteTemplateMenu';

interface INoteTemplatePickerProps {
    note: INote;
    noteTarget: INoteTarget;
    onTemplateClicked: (template: INoteTemplate, append?: boolean) => void;
}

export const NoteTemplatePicker: React.FC<INoteTemplatePickerProps>  = ({ note, noteTarget, onTemplateClicked }) => {
    const templates = useInstance(TemplateProvider).getTemplates(noteTarget);
    const mixpanel = useInstance(MixPanelEventEmitter);
    const alertPresenter = useInstance(AlertPresenter);

    const templateSelected = async (template: INoteTemplate) => {
        let action = 'insert';
        if (note.html) {
            action = await alertPresenter.showAlertV2({
                wider: true,
                backdropDismiss: true,
                header: 'Add or Replace?',
                message: 'Would you like to replace your existing content (it will be deleted), or add the template to your text?',
                options: [{
                    id: 'add',
                    label: 'Add'
                },{
                    id: 'replace',
                    label: 'Replace'
                },{
                    id: 'cancel',
                    label: 'Cancel',
                    role: 'cancel'
                }]
            });
        }

        if (action === 'cancel') {
            return;
        }

        if (action === 'insert' || action === 'replace') {
            onTemplateClicked(template);
        } else if (action === 'add') {
            onTemplateClicked(template, true);
        }

        mixpanel.track('Note Template Applied', () => ({
            action: action,
            template: template.label,
            templateId: template.templateId,
            location: getNoteTemplateTrackingLocation(template.target)
        }));
    };

    const buttonTemplates = templates.filter(t => t.isButton);
    const moreTemplates = templates.filter(t => !buttonTemplates.some(t2 => t.templateId === t2.templateId));
    const hasButtons = buttonTemplates.length > 0;

    return (
        <>
            {hasButtons && (
                <div style={{display: 'flex', paddingLeft: 12}}>
                    {buttonTemplates.map(template => (
                        <IonButton
                            fill="outline"
                            key={template.templateId}
                            onClick={() => templateSelected(template)}
                            data-id={`note-template-${template.templateId}-button`}
                        >
                            {template.label}
                        </IonButton>
                    ))}
                </div>
            )}

            <div style={{paddingLeft: 12}}>
                <NoteTemplateMenu
                    hasButtons={hasButtons}
                    templates={moreTemplates}
                    onItemClick={templateSelected}
                />
            </div>
        </>
    );
};
